/** Target value in the update-status modal that triggers bulk cancel (not bulk_update_status). */
export const PO_CANCEL_TARGET_STATUS = "Cancelled";

/** Target value in the update-status modal that hands off to the close review modal. */
export const PO_CLOSE_TARGET_STATUS = "Closed";

/** Locked end states: no receipts, edits, bills, or allocations until reopen. */
export const LOCKED_PO_STATUSES = ["Closed", "Cancelled"];

/** No manual status moves or board drag (Completed is still editable). */
export const TERMINAL_PO_STATUSES = ["Completed", "Closed", "Cancelled"];

/** PO header statuses that cannot be bulk-cancelled from the list (must match cancel modal). */
export const NOT_CANCELLABLE_PO_STATUSES = [
  "Cargo Ready",
  "Loaded",
  "Partially Dispatched",
  "Fully Dispatched",
  "In Transit",
  "Partial Receipt Created",
  "Full Receipt Created",
  "Partial Receipt Approved",
  "Full Receipt Approved",
  "Invoiced",
  "Invoice Approved",
  "Paid",
  "Completed",
  "Closed",
  "Cancelled",
];

/** Manual bulk status targets (must match backend MANUAL_LIST_STATUS_TARGETS). */
export const MANUAL_PO_STATUS_TARGETS = [
  "Sent",
  "Accepted",
  "Scheduled",
  "In Production",
  "Ready for Allocation",
  "Not Allocated",
  "Fully Allocated",
  "Cargo Ready",
];

/**
 * Full purchase-order status order. Must match PurchaseOrderStatusChoices.
 * Manual moves are allowed only while the current status is at or before Cargo Ready.
 */
export const PO_STATUS_ORDER = [
  "Draft",
  "Draft Sent",
  "Created",
  "Sent",
  "Supplier Reviewing",
  "Changes Proposed",
  "Awaiting Buyer Response",
  "Changes Rejected",
  "Accepted",
  "Scheduled",
  "In Production",
  "Ready for Allocation",
  "Not Allocated",
  "Partially Allocated",
  "Fully Allocated",
  "Cargo Ready",
  "Loaded",
  "Partially Dispatched",
  "Fully Dispatched",
  "In Transit",
  "Partial Receipt Created",
  "Full Receipt Created",
  "Partial Receipt Approved",
  "Full Receipt Approved",
  "Partial Invoice Created",
  "Full Invoice Created",
  "Partial Invoice Approved",
  "Full Invoice Approved",
  "Paid",
  "Completed",
  "Closed",
  "Cancelled",
];

const statusOrderIndex = status => {
  const index = PO_STATUS_ORDER.indexOf(status);
  return index >= 0 ? index : -1;
};

const cargoReadyIndex = statusOrderIndex("Cargo Ready");

/**
 * True when the order is still in the manually editable range, through Cargo Ready.
 *
 * @param {string} status
 * @returns {boolean}
 */
function isAtOrBeforeCargoReady(status) {
  const index = statusOrderIndex(status);
  return index >= 0 && index <= cargoReadyIndex;
}

/**
 * Manual targets later than the current status.
 *
 * @param {string} currentStatus
 * @returns {string[]}
 */
export function forwardManualPoStatusTargets(currentStatus) {
  // Draft must use Confirm Draft → Created, not jump to Fully Allocated / etc.
  if (currentStatus === "Draft" || currentStatus === "Draft Sent") {
    return [];
  }
  if (!isAtOrBeforeCargoReady(currentStatus)) {
    return [];
  }
  const currentIndex = statusOrderIndex(currentStatus);
  return MANUAL_PO_STATUS_TARGETS.filter(target => statusOrderIndex(target) > currentIndex);
}

/**
 * Manual targets earlier than the current status.
 * Available for every status at or before Cargo Ready.
 *
 * @param {string} currentStatus
 * @returns {string[]}
 */
export function backwardManualPoStatusTargets(currentStatus) {
  if (currentStatus === "Draft" || currentStatus === "Draft Sent") {
    return [];
  }
  if (!isAtOrBeforeCargoReady(currentStatus)) {
    return [];
  }
  const currentIndex = statusOrderIndex(currentStatus);
  return MANUAL_PO_STATUS_TARGETS.filter(target => statusOrderIndex(target) < currentIndex);
}

/**
 * Nearest later manual status, or the nearest earlier one when nothing later remains.
 *
 * @param {string} currentStatus
 * @returns {string|null}
 */
export function nextLogicalManualPoStatusTarget(currentStatus) {
  const forward = forwardManualPoStatusTargets(currentStatus);
  if (forward.length > 0) {
    return forward.reduce((closest, target) =>
      statusOrderIndex(target) < statusOrderIndex(closest) ? target : closest,
    );
  }

  const backward = backwardManualPoStatusTargets(currentStatus);
  if (backward.length > 0) {
    return backward.reduce((closest, target) =>
      statusOrderIndex(target) > statusOrderIndex(closest) ? target : closest,
    );
  }

  return null;
}

export function defaultManualPoStatusTarget(currentStatus, allowedTargets = []) {
  const nextLogical = nextLogicalManualPoStatusTarget(currentStatus);
  if (nextLogical && allowedTargets.includes(nextLogical)) {
    return nextLogical;
  }
  return allowedTargets[0] || "";
}

/**
 * Whether selected orders can be closed (not draft or terminal).
 *
 * @param {Array<{ status?: string }>} selectedRows
 * @returns {{ ok: boolean, reason?: string }}
 */
export function closePurchaseOrderSelectionState(selectedRows) {
  if (!selectedRows?.length) {
    return { ok: false, reason: "Select at least one purchase order." };
  }
  if (selectedRows.length > 100) {
    return { ok: false, reason: "Select at most 100 purchase orders." };
  }
  const invalid = selectedRows.find(
    row =>
      !row.status ||
      row.status === "Draft" ||
      row.status === "Draft Sent" ||
      LOCKED_PO_STATUSES.includes(row.status),
  );
  if (invalid) {
    return {
      ok: false,
      reason: "Close is only available for orders that are not already closed or cancelled.",
    };
  }
  return { ok: true };
}

export function selectedOrdersCanBeCancelled(selectedRows) {
  if (!selectedRows?.length) {
    return false;
  }
  return selectedRows.every(row => row.status && !NOT_CANCELLABLE_PO_STATUSES.includes(row.status));
}

/**
 * Status options shown in the bulk update modal.
 * At or before Cargo Ready this is every other manual status, plus optional cancel.
 *
 * @param {string} currentStatus
 * @param {{ includeCancel?: boolean }} options
 * @returns {string[]}
 */
export function bulkStatusModalTargets(currentStatus, { includeCancel = false } = {}) {
  const combined = [
    ...backwardManualPoStatusTargets(currentStatus),
    ...forwardManualPoStatusTargets(currentStatus),
  ];
  if (includeCancel) {
    combined.push(PO_CANCEL_TARGET_STATUS);
  }
  const seen = new Set();
  return combined
    .filter(target => {
      if (seen.has(target)) {
        return false;
      }
      seen.add(target);
      return true;
    })
    .sort((a, b) => statusOrderIndex(a) - statusOrderIndex(b));
}

/**
 * Whether every selected row shares one status eligible for manual bulk update.
 *
 * @param {Array<{ status?: string }>} selectedRows
 * @returns {{ ok: boolean, currentStatus?: string, reason?: string }}
 */
export function manualBulkStatusSelectionState(selectedRows) {
  if (!selectedRows?.length) {
    return { ok: false, reason: "Select at least one purchase order." };
  }
  if (selectedRows.length > 100) {
    return { ok: false, reason: "Select at most 100 purchase orders." };
  }

  const canCancel = selectedOrdersCanBeCancelled(selectedRows);
  const statuses = new Set(selectedRows.map(row => row.status).filter(Boolean));

  if (statuses.size !== 1) {
    if (canCancel) {
      return {
        ok: true,
        cancelOnly: true,
        allowedTargets: [PO_CANCEL_TARGET_STATUS],
      };
    }
    return {
      ok: false,
      reason: "All selected purchase orders must have the same status.",
    };
  }

  const currentStatus = [...statuses][0];
  if (TERMINAL_PO_STATUSES.includes(currentStatus)) {
    return { ok: false, reason: "Closed, completed, or cancelled orders cannot be updated." };
  }

  // Draft must Confirm Draft → Created first; hide Update status entirely.
  if (currentStatus === "Draft" || currentStatus === "Draft Sent") {
    return {
      ok: false,
      reason: "Confirm Draft first before updating status.",
    };
  }

  const allowedTargets = bulkStatusModalTargets(currentStatus, { includeCancel: canCancel });
  if (allowedTargets.length === 0) {
    return {
      ok: false,
      reason: "No manual status updates are available for the selected orders.",
    };
  }

  return { ok: true, currentStatus, cancelOnly: false, allowedTargets };
}

/**
 * Update status options for the list, with Closed added when the selection can be closed.
 * Closed stays available for orders past Cargo Ready (e.g. In Transit, Completed) even though
 * no manual status moves remain for them.
 *
 * @param {Array<{ status?: string }>} selectedRows
 * @returns {{
 *   ok: boolean,
 *   currentStatus?: string,
 *   cancelOnly?: boolean,
 *   allowedTargets?: string[],
 *   reason?: string,
 * }}
 */
export function purchaseOrderStatusUpdateState(selectedRows) {
  const manual = manualBulkStatusSelectionState(selectedRows);
  const close = closePurchaseOrderSelectionState(selectedRows);
  if (!close.ok) {
    return manual;
  }

  const statuses = new Set(selectedRows.map(row => row.status).filter(Boolean));
  const currentStatus = statuses.size === 1 ? [...statuses][0] : undefined;
  const manualTargets = manual.ok ? manual.allowedTargets : [];
  const allowedTargets = [...manualTargets, PO_CLOSE_TARGET_STATUS].sort(
    (a, b) => statusOrderIndex(a) - statusOrderIndex(b),
  );
  return { ok: true, currentStatus, cancelOnly: false, allowedTargets };
}
